import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
// Use the useForm hook from the library instead of our custom one
// here because we don't use any custom context on the login
// page.
// eslint-disable-next-line no-restricted-imports
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocalStorage } from 'react-use';

import { Divider } from '@/components/Divider';
import { Button } from '@/components/form/baseInputs/Button';
import { FormConfigurationProvider } from '@/components/form/context/FormConfigurationContext';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { formPost } from '@/utils/formUtils';
import { localStorageBooleanSerializer } from '@/utils/localStorageSerializers';

import { buildSsoInitiateUrl } from './LoginForm.utils';

interface LoginFormShape {
  email: string;
  rememberExtendedSession: boolean;
}

interface LoginFormProps {
  // redirectPath is the path that the user will be redirected to after they log in.
  redirectPath: string | null;
  // initialUserEmail is used to prefill the email input
  initialUserEmail: string | null;
  ssoEnabled: boolean;
}

export function LoginForm({
  redirectPath,
  initialUserEmail,
  ssoEnabled,
}: LoginFormProps) {
  const [isSSOSubmitting, setIsSSOSubmitting] = useState(false);
  const [, setRememberExtendedSession] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.REMEMBER_EXTENDED_SESSION,
    false,
    localStorageBooleanSerializer
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginFormShape>({
    defaultValues: {
      email: initialUserEmail ?? '',
      rememberExtendedSession: false,
    },
  });

  const onSubmit: SubmitHandler<LoginFormShape> = (values) => {
    setRememberExtendedSession(values.rememberExtendedSession);

    formPost('/api/auth/v2/initiate', {
      email: values.email,
      remember_extended_session: values.rememberExtendedSession,
      ...(redirectPath ? { redirect: redirectPath } : {}),
    });
    // we want to return an empty promise here so that that the react-hook-form
    // isSubmitting state flips to true. we don't worry about resolving it because
    // the form post will cause a full page reload.
    return new Promise(() => {});
  };

  function handleSSOLogin() {
    setIsSSOSubmitting(true);
    window.location.assign(buildSsoInitiateUrl(redirectPath));
  }

  const isLoginSubmitting = isSubmitting || isSSOSubmitting;

  return (
    <FormConfigurationProvider
      value={{ optionalDisplayType: 'optional-label' }}
    >
      <Stack
        noValidate
        textAlign="center"
        spacing={3}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography variant="title-s" component="p">
          Log in to Luminary
        </Typography>
        <Box textAlign="left">
          <FormAwareTextInput<LoginFormShape>
            label="Email address"
            required
            type="email"
            fieldName="email"
            control={control}
            disabled={isLoginSubmitting}
          />
          {/* hide the (optional) param */}
          <FormConfigurationProvider
            value={{ optionalDisplayType: 'required-asterisk' }}
          >
            <Box mt={1}>
              <FormAwareCheckbox<LoginFormShape>
                label="Keep me logged in for 30 days"
                fieldName="rememberExtendedSession"
                control={control}
                required={true}
                disabled={isLoginSubmitting}
              />
            </Box>
          </FormConfigurationProvider>
        </Box>
        <Button
          fullWidth
          loading={isSubmitting}
          disabled={isLoginSubmitting}
          variant="primary"
          size="sm"
          type="submit"
        >
          Continue
        </Button>
        {ssoEnabled && (
          <>
            <Divider>
              <Typography variant="body-xs">or</Typography>
            </Divider>
            <Button
              fullWidth
              loading={isSSOSubmitting}
              disabled={isLoginSubmitting}
              variant="secondary"
              size="sm"
              type="button"
              onClick={handleSSOLogin}
            >
              Log in with single sign-on
            </Button>
          </>
        )}
        <TermsAcknowledgement />
      </Stack>
    </FormConfigurationProvider>
  );
}

function TermsAcknowledgement() {
  return (
    <Typography variant="body-xs" component="div">
      By continuing, I acknowledge receipt of the <br />
      <a
        href="https://www.withluminary.com/privacy"
        target="_blank"
        rel="noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        Luminary Privacy Policy
      </a>
      &nbsp;and&nbsp;
      <a
        href="https://www.withluminary.com/terms"
        target="_blank"
        rel="noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        Luminary Terms of Service
      </a>
      .
    </Typography>
  );
}
